import { useMemo, useState } from "react";

import { Button, Link, SegmentedControl, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { usePriceConfig } from "../../hooks/usePriceConfig";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import { SearchParam, SearchParamIntent, SubscriptionPeriod, SubscriptionTier } from "../../types";
import { formatCurrency, getTaxLabel } from "../../utils/currencyUtils";

import style from "./PurchaseNotice.module.css";

interface BuySubscriptionProps {
  selectedTier: SubscriptionTier;
  selectedPeriod: SubscriptionPeriod;
  couponCode?: string;
  isTrial?: boolean;
}

interface PurchaseNoticeProps {
  isChinaVariant?: boolean;
}

export const PurchaseNotice: React.FC<PurchaseNoticeProps> = ({ isChinaVariant }) => {
  const [selectedPeriod, setSelectedPeriod] = useState(SubscriptionPeriod.Yearly);
  const { t, m } = useTranslation();
  const navigation = useNavigation();
  const tracker = useTracker();
  const { data: accountData } = useAccount();
  const { data: priceConfigData, isLoading: isPriceConfigLoading } = usePriceConfig();
  const { teamID } = useParams<{ teamID: string }>();

  const teamMembersReturnUrl = useMemo(() => {
    if (!teamID) {
      return undefined;
    }

    const url = new URL(`/teams/${teamID}/members`, window.location.origin);
    url.searchParams.set(SearchParam.Intent, SearchParamIntent.Invite);
    return url.toString();
  }, [teamID]);

  const buySubscription = ({
    selectedTier,
    selectedPeriod,
    couponCode,
    isTrial,
  }: BuySubscriptionProps) => {
    isTrial
      ? tracker.trackButtonClick({ action: ElementClickAction.PURCHASE_NOTICE_START_TRIAL })
      : tracker.trackButtonClick({ action: ElementClickAction.PURCHASE_NOTICE_SUBSCRIBE_NOW });
    navigation.goToCheckout({
      selectedTier,
      period: selectedPeriod,
      couponCode,
      returnToURL: isChinaVariant ? undefined : teamMembersReturnUrl,
    });
  };

  if (!accountData || !priceConfigData || isPriceConfigLoading) {
    return <SpinnerCenter />;
  }

  const AllProFeatures = [
    t(m.Purchase.Plans.Pro.Feature01),
    t(m.Purchase.Plans.Pro.Feature02),
    t(m.Purchase.Plans.Pro.Feature03),
    t(m.Purchase.Plans.Pro.Feature04),
    t(m.Purchase.Plans.Pro.Feature05),
    t(m.Purchase.Plans.Pro.Feature06),
    t(m.Purchase.Plans.Pro.Feature07),
  ];

  // Feature01 (Teamspace) and Feature03 (Contributors) are team/sync-specific
  const ChinaProFeatures = [
    t(m.Purchase.Plans.Pro.Feature02),
    t(m.Purchase.Plans.Pro.Feature04),
    t(m.Purchase.Plans.Pro.Feature05),
    t(m.Purchase.Plans.Pro.Feature06),
    t(m.Purchase.Plans.Pro.Feature07),
  ];
  const ProFeatures = isChinaVariant ? ChinaProFeatures : AllProFeatures;

  const isTrial =
    !isChinaVariant &&
    (accountData?.availablePlans.some((plan) => plan.trialDays > 0) ||
      accountData?.techTrialAvailable);

  const taxLabel = getTaxLabel(priceConfigData?.currencySymbol);

  const tierConfig = priceConfigData.tiers[SubscriptionTier.Lite];
  if (!tierConfig) return <SpinnerCenter />;

  const originalPrice =
    selectedPeriod === SubscriptionPeriod.Yearly
      ? Math.ceil(tierConfig.yearly / 12)
      : tierConfig.monthly;

  const formattedOriginalPrice = formatCurrency(originalPrice, priceConfigData.currencyCode);
  const formattedYearlyPrice = formatCurrency(tierConfig.yearly, priceConfigData.currencyCode);

  const billedAnnually = t(m.Purchase.PriceBilledAnnually, { taxLabel });
  const yearlyPriceBreakdown = isChinaVariant
    ? t(m.Purchase.PriceBilledAnnuallyWithPrice, { price: formattedYearlyPrice, billedAnnually })
    : t(m.Purchase.PricePerSeatBilledAnnually, { price: formattedYearlyPrice, billedAnnually });

  return (
    <div>
      <div className={style.title}>
        {isTrial ? t(m.PurchaseNotice.TryPro) : t(m.PurchaseNotice.SubscribeToPro)}
      </div>
      <SegmentedControl
        className={style.segmentedControl}
        defaultChecked={selectedPeriod === SubscriptionPeriod.Monthly}
        onChange={(e) => {
          setSelectedPeriod(
            e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly
          );
          tracker.trackButtonClick({
            action: ElementClickAction.SUBSCRIPTION_PERIOD_PICK,
            details: e.target.checked ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly,
          });
        }}
        optionA={isChinaVariant ? t(m.PurchaseNotice.OneYear) : t(m.Button.Yearly)}
        optionB={isChinaVariant ? t(m.PurchaseNotice.OneMonth) : t(m.Button.Monthly)}
        optionASecondaryText={`- ${t(m.Purchase.PriceMonthlySave, {
          savingsPercent: (tierConfig?.savingsPercent ?? 0).toFixed(0),
        })}`}
      />
      <p className={style.monthlyPrice}>
        {isChinaVariant
          ? t(m.Purchase.PricePerMonth, { price: formattedOriginalPrice })
          : t(m.Purchase.PricePerSeatPerMonth, { price: formattedOriginalPrice })}
      </p>
      <p className={style.yearlyPrice}>
        {selectedPeriod === SubscriptionPeriod.Yearly
          ? yearlyPriceBreakdown
          : t(m.Purchase.PriceBilledMonthly, { taxLabel })}
      </p>
      <div className={style.emphasized}>{t(m.Purchase.Plans.Pro.FeatureDescription)}</div>
      <ul className={style.featureList}>
        {ProFeatures.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      <div className={style.buttonContainer}>
        <Button
          onClick={() =>
            buySubscription({
              selectedPeriod,
              selectedTier: SubscriptionTier.Lite,
              isTrial,
            })
          }
        >
          {isChinaVariant
            ? t(m.PurchaseNotice.GetShapr3DPro)
            : isTrial
              ? t(m.PurchaseNotice.StartFreeTrial)
              : t(m.Checkout.SubscribeNow)}
        </Button>
        {!isChinaVariant && (
          <Link
            href="https://shapr3d.com/pricing"
            target="_blank"
            rel="noopener,noreferrer"
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.PURCHASE_NOTICE_COMPARE_SUBSCRIPTIONS,
              });
            }}
          >
            {t(m.PurchaseNotice.CompareSubscriptions)}
          </Link>
        )}
      </div>
    </div>
  );
};
